import { eventStatusTypes } from "pages/event/utils";
import {
  getContractService,
  getEventService,
  updateNewShieldEventFile,
  updateEventService,
  deleteEventGraphicSetService,
} from "../../../../services/commonService";
import { cleanSpaces } from "../../../utils/utils";
import { getDeploymentService } from "../../../../services/deploymentServices";
import { LIVESTREAM_TYPE_TIIVII_OTT } from "../common-forms/livestream-form/livestream-form-constants";

const { STARTING, STARTED, FINISHED } = eventStatusTypes;

const isTiiviiOttStream = stream =>
  stream?.type === LIVESTREAM_TYPE_TIIVII_OTT || stream?.description === LIVESTREAM_TYPE_TIIVII_OTT;

const findTiiviiOttStream = streams => streams?.find(isTiiviiOttStream);

const pickNonEmpty = obj =>
  Object.fromEntries(Object.entries(obj || {}).filter(([, v]) => v !== "" && v !== null && v !== undefined));

const LOCALE_MAP = {
  es: "es_ES",
  en: "en_US",
  gl: "gl_ES",
  ja: "ja_JP",
};

const promisifyGetContract = contractId =>
  new Promise(resolve => {
    getContractService(contractId, resolve);
  });

const promisifyGetDeployment = deploymentId =>
  new Promise((resolve, reject) => {
    getDeploymentService({ deploymentId }, resolve, reject);
  });

const getMsisdnQod = async contractId => {
  const contractData = await promisifyGetContract(contractId);
  if (!contractData?.deployment_id) return undefined;
  const deploymentData = await promisifyGetDeployment(contractData.deployment_id);
  return deploymentData?.msisdn_qod;
};

const promisifyUploadLogo = (customerId, asset) =>
  new Promise((resolve, reject) => {
    updateNewShieldEventFile({ body: { file: asset }, customerId }, resolve, reject);
  });

const promisifyUpdateEvent = (eventId, body) =>
  new Promise((resolve, reject) => {
    updateEventService(eventId, body, resolve, reject);
  });

const promisifyDeleteGraphicSet = eventId =>
  new Promise((resolve, reject) => {
    deleteEventGraphicSetService(eventId, resolve, reject);
  });

const promisifyGetEvent = eventId =>
  new Promise((resolve, reject) => {
    getEventService(eventId, resolve, reject);
  });

const mapLiveStreams = (liveStreams, eventToEdit) => {
  const originalOtt = findTiiviiOttStream(eventToEdit?.liveStreams);

  return (
    liveStreams?.map(stream => {
      const source = isTiiviiOttStream(stream) && originalOtt ? { ...originalOtt, ...pickNonEmpty(stream) } : stream;

      return {
        type: source.type,
        description: source.description,
        rtmp_url: source.rtmpUrl,
        key: source.key,
        publishing_url: source.publishingUrl,
        ...(source.componentName != null && source.componentName !== "" && { component_name: source.componentName }),
        ...(source.componentStatus != null && source.componentStatus !== "" && { component_status: source.componentStatus }),
      };
    }) ?? []
  );
};

const mapSportEventData = sportEventData => ({
  team_name_1: sportEventData.teamName1 || "",
  team_name_1_abreviated: sportEventData.teamName1Abreviated || "",
  team_logo_1: sportEventData.teamLogo1 || "",
  team_name_2: sportEventData.teamName2 || "",
  team_name_2_abreviated: sportEventData.teamName2Abreviated || "",
  team_logo_2: sportEventData.teamLogo2 || "",
  alt_scoreboard_template_id: sportEventData.scoreboardTemplateId || "",
  scoreboard_type: sportEventData.scoreboardType || "",
});

const mapMatchPointConfig = matchPointConfig => ({
  ocurrences_source: matchPointConfig.ocurrencesSource,
  ocurrences_source_id: matchPointConfig.ocurrencesSourceId,
  ocurrences_visible: matchPointConfig.ocurrencesVisible,
});

const mapSubdeployment = subdeployment => ({
  id: subdeployment.id || subdeployment._id || "",
  name: subdeployment.name || "",
  description: subdeployment.description || "",
  streamsource_ids: subdeployment.streamsourceIds || subdeployment.streamsource_ids || [],
});

export const handleSubmit = ({ history, eventToEdit }) => async values => {
  const eventId = eventToEdit.id;
  const isSportEvent = eventToEdit.type?.isSportEvent;

  let sportEventDataPayload = null;

  if (isSportEvent && values.sportEventData) {
    const sportData = values.sportEventData;
    let teamLogo1 = sportData.teamLogo1 || "";
    let teamLogo2 = sportData.teamLogo2 || "";

    const hasNewLogo1 = !teamLogo1 && sportData.teamLogoAsset1?.image;
    const hasNewLogo2 = !teamLogo2 && sportData.teamLogoAsset2?.image;

    if (hasNewLogo1 || hasNewLogo2) {
      const contractData = await promisifyGetContract(eventToEdit.contractId);
      const logoCustomerId = contractData.customer_id;

      const logoPromises = [];

      if (hasNewLogo1) {
        logoPromises.push(
          promisifyUploadLogo(logoCustomerId, sportData.teamLogoAsset1).then(result => {
            teamLogo1 = result._id;
          }),
        );
      }

      if (hasNewLogo2) {
        logoPromises.push(
          promisifyUploadLogo(logoCustomerId, sportData.teamLogoAsset2).then(result => {
            teamLogo2 = result._id;
          }),
        );
      }

      await Promise.all(logoPromises);
    }

    sportEventDataPayload = mapSportEventData({
      ...sportData,
      teamLogo1,
      teamLogo2,
    });
  }

  let localRecInfoPayload = null;
  let localRecEmails =
    !!values?.localRecInfo?.local_rec_emails && cleanSpaces(values.localRecInfo.local_rec_emails).split(",");
  if (!localRecEmails && !!values?.localRecInfo?.userEmails) {
    localRecEmails = values.localRecInfo.userEmails.map(userEmail => userEmail.email);
  }
  if (localRecEmails && localRecEmails.length) {
    localRecInfoPayload = {
      user_emails: localRecEmails.map(email => ({ email: email.trim(), locale: "es_ES" })),
    };
  }

  const subdeploymentPayload = values.subdeployment ? mapSubdeployment(values.subdeployment) : null;

  const matchPointConfigPayload = values.matchPointConfig?.ocurrencesSource
    ? mapMatchPointConfig(values.matchPointConfig)
    : null;

  const msisdnQod = values.activeQod ? await getMsisdnQod(eventToEdit.contractId) : undefined;

  let graphicSetId = values.graphicSetId ? values.graphicSetId : null;
  switch (eventToEdit.eventStatus) {
    case STARTING:
    case STARTED:
    case FINISHED:
      graphicSetId = eventToEdit.graphicSetId;
      break;
  }

  const body = {
    _id: eventId,
    service_contract_id: eventToEdit.contractId,
    event: {
      title: values.title,
      description: values.description,
      start_date: Math.floor(new Date(values.startDate).getTime() / 1000),
      end_date: Math.floor(new Date(values.endDate).getTime() / 1000),
    },
    livestreams: mapLiveStreams(values.liveStreams, eventToEdit),
    ...(sportEventDataPayload && { sport_event_data: sportEventDataPayload }),
    ...(matchPointConfigPayload && { match_point_config: matchPointConfigPayload }),
    ...(subdeploymentPayload && { subdeployment: subdeploymentPayload }),
    ...(localRecInfoPayload && { local_rec_info: localRecInfoPayload }),
    ...(graphicSetId && { graphic_set_id: graphicSetId }),
    visible: values.visible,
    billable: values.billable,
    active_qod: values.activeQod || false,
    is_muted_on_start: values.isMutedOnStart || false,
    unmute_minutes_before: values.isMutedOnStart ? Number(values.unmuteMinutesBefore ?? 0) : undefined,
    ...(values.activeQod && msisdnQod && { msisdn_qod: msisdnQod }),
    local_rec: values.localRec || false,
    authorized_recording: values.authorizedRecording || false,
    is_internal_test: values.isInternalTest || false,
    keywords: values.keywords || [],
    email_receipts: values.emailReceipts ? values.emailReceipts.map(entity => entity.email) : [],
    email_locale: values.emailReceipts
      ? values.emailReceipts.map(entity => LOCALE_MAP[entity.locale] ?? entity.locale ?? "es_ES")
      : [],
    room_template_id: values.roomTemplateId,
  };

  await promisifyUpdateEvent(eventId, body);

  const graphicSetWasCleared = eventToEdit.graphicSetId && !graphicSetId;
  if (graphicSetWasCleared) {
    await promisifyDeleteGraphicSet(eventId);
  }

  await promisifyGetEvent(eventId);

  history.goBack();
};
